import { Injectable } from '@angular/core';
import { Funcionario } from '../../api.service';
import { ModoCameraFacial } from '../../shared/camera-facial.component';
import { CaixaFaceDetectada } from '../../shared/servico-reconhecimento-facial';

export interface EstadoFaceFuncionario {
  cameraAtiva: boolean;
  cameraModo: ModoCameraFacial;
  rostoDetectado: boolean;
  provaVidaOk: boolean;
  capturando: boolean;
  mensagemCamera: string;
  caixaFace?: CaixaFaceDetectada;
  detectorDisponivel: boolean;
  descritorFacialAtual: number[];
  registroAutomaticoDisparado: boolean;
  leiturasFaceValidacao: number;
  validacaoCooldownAte: number;
}

@Injectable({ providedIn: 'root' })
export class ServicoFaceFuncionario {
  readonly limiteFacesReferencia = 4;

  statusFaceReferencia(funcionario: Funcionario) {
    if (funcionario.faces) {
      return funcionario.faces.some(face => face.ativo) ? 'capturada' : 'nao capturada';
    }
    const temImagem = Boolean(funcionario.imagemFacialBase64);
    const temDescritor = Boolean(funcionario.faceCadastrada);
    return temImagem || temDescritor ? 'capturada' : 'nao capturada';
  }

  estadoAbertura(modo: ModoCameraFacial): EstadoFaceFuncionario {
    return {
      ...this.estadoBase(modo),
      cameraAtiva: true,
      mensagemCamera: 'Carregando reconhecimento facial.'
    };
  }

  estadoFechado(modoAtual: ModoCameraFacial): EstadoFaceFuncionario {
    return this.estadoBase(modoAtual);
  }

  atualizarLeiturasValidacao(
    modo: ModoCameraFacial,
    rostoDetectado: boolean,
    descritorFacial: number[],
    leiturasAtuais: number
  ) {
    if (modo !== 'validacao') {
      return leiturasAtuais;
    }
    return rostoDetectado && descritorFacial.length > 0 ? leiturasAtuais + 1 : 0;
  }

  deveDispararValidacaoAutomatica(modo: ModoCameraFacial, leituras: number, validacaoDisparada: boolean) {
    return modo === 'validacao' && leituras >= 3 && !validacaoDisparada;
  }

  capturaValida(
    modo: ModoCameraFacial,
    rostoDetectado: boolean,
    provaVidaOk: boolean,
    descritorFacial: number[]
  ) {
    if (modo === 'validacao') {
      return rostoDetectado && descritorFacial.length > 0;
    }

    return rostoDetectado && provaVidaOk && descritorFacial.length > 0;
  }

  mensagemValidando(nomeFuncionario: string) {
    return `Rosto detectado. Validando ${nomeFuncionario}.`;
  }

  mensagemFuncionarioEncontrado(nomeFuncionario: string) {
    return `Funcionario encontrado: ${nomeFuncionario}.`;
  }

  orientacaoCadastro(quantidadeFacesAtivas: number) {
    const etapa = Math.min(quantidadeFacesAtivas + 1, this.limiteFacesReferencia);
    const orientacoes = [
      'Olhe de frente para a camera.',
      'Vire levemente o rosto para a esquerda.',
      'Vire levemente o rosto para a direita.',
      'Levante levemente o rosto.'
    ];
    return `Referencia ${etapa}/${this.limiteFacesReferencia}: ${orientacoes[etapa - 1]}`;
  }

  mensagemCadastroCamera(quantidadeFacesAtivas: number, mensagemAnalise: string) {
    return `${this.orientacaoCadastro(quantidadeFacesAtivas)} ${mensagemAnalise}`;
  }

  aplicarFalhaValidacao(estado: EstadoFaceFuncionario, mensagemErro: string): EstadoFaceFuncionario {
    return {
      ...estado,
      capturando: false,
      mensagemCamera: mensagemErro,
      registroAutomaticoDisparado: false,
      leiturasFaceValidacao: 0,
      validacaoCooldownAte: Date.now() + 5000
    };
  }

  aplicarErroAnalise(estado: EstadoFaceFuncionario): EstadoFaceFuncionario {
    return {
      ...estado,
      rostoDetectado: false,
      caixaFace: undefined,
      leiturasFaceValidacao: 0,
      mensagemCamera: 'Nao foi possivel analisar o rosto.'
    };
  }

  private estadoBase(modo: ModoCameraFacial): EstadoFaceFuncionario {
    return {
      cameraAtiva: false,
      cameraModo: modo,
      rostoDetectado: false,
      provaVidaOk: false,
      capturando: false,
      mensagemCamera: '',
      caixaFace: undefined,
      detectorDisponivel: false,
      descritorFacialAtual: [],
      registroAutomaticoDisparado: false,
      leiturasFaceValidacao: 0,
      validacaoCooldownAte: 0
    };
  }
}
